/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import EnhancedTable from "components/common_components/Tables/EnhancedTable";
import { Button } from "@material-ui/core";
import Page from "components/common_components/Page/Page";
import useStyles from "../useStyles";
import { useDispatch, useSelector } from "react-redux";
import { notifyError, notifyInfo, notifySuccess } from "redux/actions/Notifications";
import LoadingPage from "components/common_components/LoadingPage";
import ModulesContext from "../ModulesContext";
import { FormDialog } from "components/common_components/Dialogs";
import { Form as FormikForm, TextField, DateTimePickerField, AutocompleteField, CheckboxField } from "components/common_components/Formik";
import * as Yup from "yup";
import Grid from "@material-ui/core/Grid";
import PostAddIcon from "@material-ui/icons/PostAdd";
import AuthRequiredDialog from "../Accounting/AuthRequiredDialog";
import { getDateMillis } from "utils/dateUtils";

const getCommentStyle = (data) => {
    if (data.isImportant) {
        return {
            color: "red"
        }
    }
    if (data.isAttorney) {
        return {
            color: "blue",
        };
    }
    return undefined;
}

const headCells = [
    { index: 1, id: "dateString", orderBy: "date", disablePadding: false, label: "Date" },
    { index: 2, id: "type.type", disablePadding: false, label: "Type" },
    {
        index: 3,
        id: "comments",
        disablePadding: false,
        label: "Comments",
        render: (_, { value, data }) => {
            return <div style={getCommentStyle(data)}>{value}</div>;
        },
    },
    { index: 4, id: "user.name", disablePadding: false, label: "User" },
];

function ProgressInfo({ onCancel }) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.Login);
    const [formInitialValues, setFormInitialValues] = useState(undefined);
    const [mode, setMode] = useState("add");
    const [formDialogProps, setFormDialogProps] = useState({
        open: false,
        title: "",
        formId: "in-dialog-form",
        isSubmitting: false,
        onClose: () => {
            setFormInitialValues(undefined);
            setFormDialogProps((prev) => ({ ...prev, open: false }));
        },
    });
    const [authRequiredDialogProps, setAuthRequiredDialogProps] = useState({
        open: false,
        onAuthenticated: undefined,
        onClose: () => {
            setAuthRequiredDialogProps((prev) => ({ ...prev, open: false }));
        },
    });

    const {
        ProgressInfo,
        currentFileCase,
        getProgressInfo,
        progressInfoData: data,
        pageableProgressInfoData,
        resources: { contactTypes },
        isLoading,
    } = useContext(ModulesContext);

    useEffect(() => {
        if (!currentFileCase) {
            dispatch(notifyError("You have to select a client file first!"));
            onCancel();
        }
    }, [currentFileCase]);

    function onDeleteSuccess() {
        dispatch(notifyInfo("Removed successfully!"));
        getProgressInfo();
    }

    function onDeleteError(error) {
        dispatch(notifyError(error.message));
    }

    function onDeleteHandler(selectedItems) {
        setAuthRequiredDialogProps((prev) => ({
            ...prev,
            open: true,
            onAuthenticated: () => {
                ProgressInfo.deleteAll({
                    ids: selectedItems,
                    onSuccess: onDeleteSuccess,
                    onError: onDeleteError,
                });
            },
        }));
    }

    const validationSchema = Yup.object({
        date: Yup.date().required("Required field"),
        type: Yup.object().required("Required field"),
        comments: Yup.string().max(2000).required("Required field"),
        isImportant: Yup.bool(),
    });

    function addProgress() {
        setMode("add");
        setFormInitialValues({
            date: new Date(),
            type: null,
            comments: "",
            customerId: currentFileCase.client.id,
            isImportant: false,
        });
        setFormDialogProps((prev) => ({ ...prev, open: true, title: "Add Progress Information" }));
    }

    function updateProgress(item) {
        if (user.isAdmin) {
            setMode("update");
            setFormInitialValues(item);
            setFormDialogProps((prev) => ({ ...prev, open: true, title: "Update Progress Information" }));
        }
    }

    function onSuccess(_, setSubmitting) {
        setSubmitting(false);
        setFormDialogProps((prev) => ({ ...prev, isSubmitting: false }));
        dispatch(notifySuccess("Progress Information saved successfully!"));
        formDialogProps.onClose();
        getProgressInfo();
    }

    function onError(error, setSubmitting, setErrors) {
        if (error && error.errors) {
            setErrors(error.errors);
            dispatch(notifyError(error.message));
        } else {
            dispatch(notifyError(error));
            formDialogProps.onClose();
        }
        setSubmitting(false);
        setFormDialogProps((prev) => ({ ...prev, isSubmitting: false }));
    }

    async function onSubmitHandler(values, { setSubmitting, setErrors }) {
        const { date, type, ...payload } = values;

        const dateInMillis = getDateMillis(date);

        const apiCall = mode === "add" ? ProgressInfo.add : ProgressInfo.update;

        setSubmitting(true);
        setFormDialogProps((prev) => ({ ...prev, isSubmitting: true }));

        apiCall({
            payload: {
                ...payload,
                date: dateInMillis,
                fileCaseId: currentFileCase.id,
                contactTypeId: type.id,
                userId: user.id,
            },
            onSuccess: (response) => onSuccess(response, setSubmitting),
            onError: (error) => onError(error, setSubmitting, setErrors),
        });
    }

    function filterBuilder(filterValue) {
        return filterValue
            ? {
                  comments: filterValue,
              }
            : {};
    }

    return (
        <Page className={classes.root}>
            {isLoading ? (
                <LoadingPage />
            ) : (
                <>
                    <Grid container spacing={3} style={{ justifyContent: "space-between" }}>
                        <Grid item md={6}>
                            <Button variant="text" size="small" color="primary" startIcon={<PostAddIcon />} onClick={addProgress}>
                                Add Progress Information
                            </Button>
                        </Grid>
                        <Grid item xs={12}>
                            <EnhancedTable
                                headCells={headCells}
                                data={data}
                                title="Progress Information"
                                defaultOrder={{ order: "asc", orderBy: "dateString" }}
                                keyColumnName="id"
                                selectable={user.isAdmin}
                                onRowClick={updateProgress}
                                deletable={user.isAdmin}
                                onDelete={onDeleteHandler}
                                onRequestData={getProgressInfo}
                                filterBuilder={filterBuilder}
                                pageableData={pageableProgressInfoData}
                                defaultRowsPerPage={15}
                            />
                        </Grid>
                    </Grid>
                    <FormDialog {...formDialogProps}>
                        {formInitialValues && (
                            <FormikForm
                                id="in-dialog-form"
                                onSubmit={onSubmitHandler}
                                validateOnChange={false}
                                initialValues={formInitialValues}
                                validationSchema={validationSchema}
                                showButtons={false}
                            >
                                <Grid container spacing={2}>
                                    <Grid item xs={5}>
                                        <DateTimePickerField fieldName="date" label="Date *" />
                                    </Grid>
                                    <Grid item xs={7}>
                                        <AutocompleteField
                                            fieldName="type"
                                            label="Type *"
                                            options={contactTypes}
                                            valueKey="code"
                                            labelKey="type"
                                            getOptionLabel={({ type }) => type}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <CheckboxField fieldName="isImportant" label="Is Important" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <CheckboxField fieldName="isAttorney" label="Attorney's comment" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField fieldName="comments" label="Comments *" multiline minRows={10} />
                                    </Grid>
                                </Grid>
                            </FormikForm>
                        )}
                    </FormDialog>
                    <AuthRequiredDialog {...authRequiredDialogProps} />
                </>
            )}
        </Page>
    );
}

ProgressInfo.propTypes = {
    onCancel: PropTypes.func,
};

ProgressInfo.defaultProps = {
    onCancel: () => {},
};

export default withRouter(ProgressInfo);
